<template>
  <div class="kassa-after-redirect">
    <div class="header-main">
      <div class="hm-left">
        <div class="header-main__title-wrap">
          <h1 class="header-main__title">Касса после статуса «Доставка курьером»</h1>
        </div>
      </div>
    </div>

    <div class="controls no-print">
      <div class="filter-row">
        <label class="filter-label">Период касс:</label>
        <span class="period-label">с</span>
        <input
          v-model="dateFromStr"
          type="date"
          class="filter-input period-date-input"
        />
        <span class="period-label">по</span>
        <input
          v-model="dateToStr"
          type="date"
          class="filter-input period-date-input"
        />
        <button
          class="btn-load"
          :disabled="loading || (!dateFromStr && !dateToStr)"
          @click="load"
        >
          {{ loading ? 'Загрузка...' : 'Загрузить' }}
        </button>
        <button
          v-if="ordersForPrint.length"
          class="btn-print"
          :disabled="printing"
          @click="printKassa"
        >
          {{ printing ? 'Печать...' : 'Печать кассы (данные на момент «Доставка курьером»)' }}
        </button>
      </div>
      <div class="filter-row dates-with-kassa no-print">
        <span class="filter-label">Даты с кассами:</span>
        <button
          type="button"
          class="btn-dates"
          :disabled="datesLoading || !dateFromStr || dateFromStr.length < 7"
          @click="loadDatesWithKassa"
        >
          {{ datesLoading ? 'Загрузка...' : 'Смотреть' }}
        </button>
        <div v-if="datesWithKassa.length" class="dates-pills">
          <button
            v-for="d in datesWithKassa"
            :key="d"
            type="button"
            class="date-pill"
            :class="{ active: dateFromStr === d && dateToStr === d }"
            @click="pickDate(d)"
          >
            {{ formatDateShort(d) }}
          </button>
        </div>
      </div>
      <div v-if="loaded && orders.length" class="filter-row filter-filters no-print">
        <label class="filter-label">Что изменилось:</label>
        <select v-model="filterCompositionChanged" class="filter-select">
          <option value="">Все</option>
          <option value="yes">Есть изменения</option>
          <option value="no">Без изменений</option>
          <option value="composition">Состав товаров</option>
          <option value="price">Цена/кол-во</option>
          <option value="payment_type">Способ оплаты</option>
          <option value="payment_amount">Сумма оплаты</option>
        </select>
        <label class="filter-label">Разница в цене:</label>
        <select v-model="filterPriceDiff" class="filter-select">
          <option value="">Все</option>
          <option value="yes">Есть разница</option>
          <option value="no">Нет разницы</option>
        </select>
      </div>
      <p v-if="loaded && orders.length" class="orders-count no-print">
        Заказов: {{ filteredOrders.length }}{{ filteredOrders.length !== orders.length ? ' из ' + orders.length : '' }}
      </p>
      <p v-if="error" class="error-msg">{{ error }}</p>
    </div>

    <div v-if="loaded && !orders.length && !loading" class="empty-msg no-print">
      За выбранный период заказов по фильтру «Касса» не найдено.
    </div>
    <div ref="printArea" class="print-area">
      <div v-if="loaded && orders.length" class="report-header print-only">
        <h2>Касса после статуса «Доставка курьером» — {{ periodTitle }}</h2>
      </div>

      <div v-if="loaded && orders.length" class="table-wrap">
        <table class="report-table">
          <thead>
            <tr>
              <th class="th-sortable" :class="{ sorted: sortBy === 'number' }" @click="setSort('number')">
                № заказа <span v-if="sortBy === 'number'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ sorted: sortBy === 'status' }" @click="setSort('status')">
                Статус заказа <span v-if="sortBy === 'status'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ sorted: sortBy === 'paymentType' }" @click="setSort('paymentType')">
                Тип оплаты <span v-if="sortBy === 'paymentType'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th>Состав до статуса «Доставка курьером»</th>
              <th>Текущий состав (manager.1akb.by)</th>
              <th class="th-sortable" :class="{ sorted: sortBy === 'totalAtRedirect' }" @click="setSort('totalAtRedirect')" title="Сумма и типы оплаты на момент «Доставка курьером»">
                Итого до <span class="th-sub">(сумма / оплата)</span>
                <span v-if="sortBy === 'totalAtRedirect'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ sorted: sortBy === 'totalCurrent' }" @click="setSort('totalCurrent')" title="Текущая сумма и типы оплаты">
                Итого после <span class="th-sub">(сумма / оплата)</span>
                <span v-if="sortBy === 'totalCurrent'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ sorted: sortBy === 'totalDiff' }" @click="setSort('totalDiff')">
                Разница (руб) <span v-if="sortBy === 'totalDiff'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ sorted: sortBy === 'whatChanged' }" @click="setSort('whatChanged')">
                Что изменилось <span v-if="sortBy === 'whatChanged'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedOrders" :key="row.id">
              <td class="cell-number">
                <router-link :to="'/edit/' + row.id" target="_blank" class="order-link">{{ row.number }}</router-link>
              </td>
              <td class="cell-status">{{ row.status || '—' }}</td>
              <td class="cell-payment-type">{{ row.paymentType || '—' }}</td>
              <td class="cell-items">
                <div v-for="(it, i) in row.itemsAtRedirect" :key="'at-' + i" class="item-line" :class="{ 'item-removed': isItemRemoved(row, it) }">
                  {{ it.name }} — {{ it.quantity }} × <span :class="{ 'item-price-red': isItemPriceChangedBefore(row, it) }">{{ formatPrice(it.price) }}</span> = <span :class="{ 'item-price-red': isItemPriceChangedBefore(row, it) }">{{ formatPrice(it.sum) }}</span>
                </div>
                <span v-if="!row.itemsAtRedirect.length">—</span>
              </td>
              <td class="cell-items">
                <div v-for="(it, i) in row.itemsCurrent" :key="'cur-' + i" class="item-line" :class="{ 'item-added': isItemAdded(row, it) }">
                  {{ it.name }} — {{ it.quantity }} × <span :class="{ 'item-price-red': isItemPriceChangedAfter(row, it) }">{{ formatPrice(it.price) }}</span> = <span :class="{ 'item-price-red': isItemPriceChangedAfter(row, it) }">{{ formatPrice(it.sum) }}</span>
                </div>
                <span v-if="!row.itemsCurrent.length">—</span>
              </td>
              <td class="cell-total cell-total-with-pay">
                <div class="total-sum">{{ formatPrice(row.totalAtRedirect) }}</div>
                <div v-if="row.paymentTypesAtRedirect" class="total-payment-types">{{ row.paymentTypesAtRedirect }}</div>
                <div v-else class="total-payment-types muted">—</div>
              </td>
              <td class="cell-total cell-total-with-pay">
                <div class="total-sum">{{ formatPrice(row.totalCurrent) }}</div>
                <div v-if="row.paymentTypesAfter || row.paymentType" class="total-payment-types">{{ row.paymentTypesAfter || row.paymentType }}</div>
                <div v-else class="total-payment-types muted">—</div>
              </td>
              <td class="cell-diff" :class="row.totalDiff !== 0 ? 'has-diff' : ''">
                {{ formatPrice(row.totalDiff) }}
              </td>
              <td class="cell-changed">
                <template v-if="!row.whatChanged || !row.whatChanged.length">
                  <span class="badge-no">Нет</span>
                </template>
                <template v-else>
                  <div v-for="(ch, ci) in row.whatChanged" :key="ci" class="change-line">
                    <span :class="'badge-tag badge-' + ch.type">{{ changeTypeLabel(ch.type) }}</span>
                    <span v-if="ch.who" class="change-who">{{ ch.who }}</span>
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { ORDERS_API_BASE } from '@/config/api';
import axios from 'axios';

const API_URL = ORDERS_API_BASE + '/kassa_after_redirect.php';

export default {
  name: 'KassaAfterRedirect',
  data() {
    const def = this.getDefaultDate();
    return {
      dateFromStr: def,
      dateToStr: def,
      loading: false,
      loaded: false,
      orders: [],
      ordersForPrint: [],
      totalFound: 0,
      error: null,
      printing: false,
      filterCompositionChanged: '',
      filterPriceDiff: '',
      sortBy: 'number',
      sortDir: 'asc',
      datesWithKassa: [],
      datesLoading: false,
    };
  },
  computed: {
    periodTitle() {
      if (!this.dateFromStr && !this.dateToStr) return '';
      const from = this.dateFromStr;
      const to = this.dateToStr || from;
      const fmt = (ymd) => {
        if (!ymd || ymd.length < 10) return ymd;
        const [y, m, d] = ymd.split('-');
        return `${d}.${m}.${y}`;
      };
      return from === to ? fmt(from) : `с ${fmt(from)} по ${fmt(to)}`;
    },
    sortedOrders() {
      const list = this.filteredOrders || [];
      const by = this.sortBy;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return list.slice().sort((a, b) => {
        let va = a[by];
        let vb = b[by];
        if (by === 'number') {
          va = (a.number || '').toString();
          vb = (b.number || '').toString();
          return dir * (va.localeCompare(vb, undefined, { numeric: true }) || 0);
        }
        if (by === 'totalAtRedirect' || by === 'totalCurrent' || by === 'totalDiff') {
          va = Number(va);
          vb = Number(vb);
          if (isNaN(va)) va = 0;
          if (isNaN(vb)) vb = 0;
          return dir * (va - vb);
        }
        if (by === 'whatChanged') {
          const la = (a.whatChanged && a.whatChanged.length) || 0;
          const lb = (b.whatChanged && b.whatChanged.length) || 0;
          return dir * (la - lb);
        }
        va = (va != null ? va : '').toString();
        vb = (vb != null ? vb : '').toString();
        return dir * (va.localeCompare(vb) || 0);
      });
    },
    filteredOrders() {
      var list = this.orders || [];
      var f = this.filterCompositionChanged;
      if (f === 'yes') {
        list = list.filter(function (row) { return row.whatChanged && row.whatChanged.length > 0; });
      } else if (f === 'no') {
        list = list.filter(function (row) { return !row.whatChanged || row.whatChanged.length === 0; });
      } else if (f === 'composition' || f === 'price' || f === 'payment_type' || f === 'payment_amount') {
        list = list.filter(function (row) {
          return row.whatChanged && row.whatChanged.some(function (ch) { return ch.type === f; });
        });
      }
      if (this.filterPriceDiff === 'yes') {
        list = list.filter(function (row) { return row.totalDiff != null && row.totalDiff !== 0; });
      } else if (this.filterPriceDiff === 'no') {
        list = list.filter(function (row) { return row.totalDiff == null || row.totalDiff === 0; });
      }
      return list;
    },
  },
  watch: {
    dateFromStr(val, oldVal) {
      const m1 = val && val.length >= 7 ? val.substring(0, 7) : '';
      const m2 = oldVal && oldVal.length >= 7 ? oldVal.substring(0, 7) : '';
      if (m1 !== m2) this.datesWithKassa = [];
    },
  },
  methods: {
    setSort(field) {
      if (this.sortBy === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortDir = 'asc';
      }
    },
    formatDateShort(ymd) {
      if (!ymd || ymd.length < 10) return ymd;
      const parts = ymd.split('-');
      return parts[2] + '.' + parts[1];
    },
    pickDate(ymd) {
      this.dateFromStr = ymd;
      this.dateToStr = ymd;
      this.load();
    },
    async loadDatesWithKassa() {
      const month = this.dateFromStr && this.dateFromStr.length >= 7
        ? this.dateFromStr.substring(0, 7)
        : '';
      if (!month) return;
      this.datesLoading = true;
      try {
        const { data } = await axios.get(API_URL, { params: { action: 'dates', month: month } });
        if (data.success && Array.isArray(data.dates)) {
          this.datesWithKassa = data.dates;
        } else {
          this.datesWithKassa = [];
        }
      } catch (_) {
        this.datesWithKassa = [];
      } finally {
        this.datesLoading = false;
      }
    },
    getDefaultDate() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    changeTypeLabel(type) {
      var map = {
        composition: 'Состав товаров',
        price: 'Цена/кол-во',
        payment_type: 'Способ оплаты',
        payment_amount: 'Сумма оплаты'
      };
      return map[type] || type;
    },
    itemNameKey(name) {
      return (name || '').toString().trim();
    },
    isItemRemoved(row, it) {
      if (!row.removed || !row.removed.length) return false;
      var key = this.itemNameKey(it.name);
      return row.removed.some(function (r) { return (r.name || '').toString().trim() === key; });
    },
    isItemPriceChangedBefore(row, it) {
      if (!row.priceOrQtyChanged || !row.priceOrQtyChanged.length) return false;
      var key = this.itemNameKey(it.name);
      return row.priceOrQtyChanged.some(function (x) { return (x.before && (x.before.name || '').toString().trim() === key); });
    },
    isItemAdded(row, it) {
      if (!row.added || !row.added.length) return false;
      var key = this.itemNameKey(it.name);
      return row.added.some(function (r) { return (r.name || '').toString().trim() === key; });
    },
    isItemPriceChangedAfter(row, it) {
      if (!row.priceOrQtyChanged || !row.priceOrQtyChanged.length) return false;
      var key = this.itemNameKey(it.name);
      return row.priceOrQtyChanged.some(function (x) { return (x.after && (x.after.name || '').toString().trim() === key); });
    },
    formatPrice(val) {
      if (val == null || val === '') return '0';
      const n = Number(val);
      return isNaN(n) ? '0' : n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },
    async load() {
      if (!this.dateFromStr && !this.dateToStr) return;
      this.loading = true;
      this.error = null;
      this.orders = [];
      this.ordersForPrint = [];
      this.loaded = false;
      try {
        let from = this.dateFromStr || this.dateToStr;
        let to = this.dateToStr || this.dateFromStr || from;
        if (from && to && from > to) {
          const t = from;
          from = to;
          to = t;
        }
        const { data } = await axios.post(API_URL, { date_from: from, date_to: to });
        if (data.success && Array.isArray(data.orders)) {
          this.orders = data.orders;
          this.ordersForPrint = Array.isArray(data.ordersForPrint) ? data.ordersForPrint : [];
          this.totalFound = data.totalFound != null ? data.totalFound : data.orders.length;
        } else {
          this.error = data.message || 'Ошибка загрузки';
          this.ordersForPrint = [];
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Ошибка запроса';
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },
    print() {
      window.print();
    },
    async printKassa() {
      if (!this.ordersForPrint.length) return;
      this.printing = true;
      try {
        const dateForTitle = this.periodTitle || '';
        const response = await axios.post(ORDERS_API_BASE + '/generate_kassa.php', {
          orders: this.ordersForPrint,
          date: dateForTitle
        }, {
          timeout: 60000,
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.data && response.data.success && response.data.url) {
          window.open(response.data.url, '_blank');
        } else {
          this.error = (response.data && response.data.error) || 'Ошибка генерации печатной формы';
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.error) || e.message || 'Ошибка запроса';
      } finally {
        this.printing = false;
      }
    },
  },
};
</script>

<style scoped>
.kassa-after-redirect {
  padding: 0 16px 24px;
}

.header-main {
  margin-bottom: 16px;
}

.controls {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-label {
  font-weight: 500;
}

.filter-input,
.filter-select {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.filter-filters {
  margin-top: 10px;
}

.filter-filters .filter-label {
  margin-left: 12px;
}

.filter-filters .filter-label:first-of-type {
  margin-left: 0;
}

.dates-with-kassa {
  margin-top: 8px;
  align-items: flex-start;
}

.dates-with-kassa .filter-label {
  flex-shrink: 0;
}

.btn-dates {
  padding: 6px 14px;
  border-radius: 4px;
  border: 1px solid #005eeb;
  background: #fff;
  color: #005eeb;
  cursor: pointer;
  font-size: 14px;
  flex-shrink: 0;
}

.btn-dates:hover:not(:disabled) {
  background: #f0f6ff;
}

.btn-dates:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.dates-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.date-pill {
  padding: 4px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #f8f8f8;
  font-size: 13px;
  cursor: pointer;
}

.date-pill:hover {
  background: #eee;
  border-color: #999;
}

.date-pill.active {
  background: #005eeb;
  color: #fff;
  border-color: #005eeb;
}

.period-label {
  font-weight: 500;
  color: #555;
}

.period-date-input {
  width: 160px;
}

.th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.th-sortable:hover {
  background: #e8e8e8;
}

.th-sortable.sorted {
  background: #e0e8f0;
}

.sort-arrow {
  margin-left: 4px;
  font-size: 12px;
}

.btn-load,
.btn-print {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.btn-load {
  background: #005eeb;
  color: #fff;
}

.btn-load:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-print {
  background: #2d7d46;
  color: #fff;
}

.orders-count {
  margin: 8px 0 0;
  font-weight: 500;
  color: #333;
}

.error-msg {
  color: #c00;
  margin-top: 8px;
}

.empty-msg {
  color: #666;
}

.limit-msg {
  color: #666;
  font-size: 13px;
  margin-bottom: 12px;
}

.table-wrap {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.report-table th,
.report-table td {
  border: 1px solid #ddd;
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
}

.report-table th {
  background: #f5f5f5;
  font-weight: 600;
}

.cell-number {
  white-space: nowrap;
}

.order-link {
  color: #005eeb;
  text-decoration: none;
}

.order-link:hover {
  text-decoration: underline;
}

.cell-items {
  min-width: 200px;
}

.item-line {
  margin-bottom: 4px;
}

.item-removed {
  color: #c00;
}

.item-added {
  color: #0a6b0a;
}

.item-price-red {
  color: #c00;
  font-weight: 600;
}

.cell-total {
  white-space: nowrap;
  text-align: right;
}

.cell-total-with-pay {
  white-space: normal;
  vertical-align: top;
  min-width: 120px;
}

.cell-total-with-pay .total-sum {
  font-weight: 600;
  margin-bottom: 4px;
}

.cell-total-with-pay .total-payment-types {
  font-size: 11px;
  line-height: 1.35;
  color: #444;
  text-align: right;
  word-break: break-word;
}

.cell-total-with-pay .total-payment-types.muted {
  color: #aaa;
}

.th-sub {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #666;
  margin-top: 2px;
}

.cell-diff {
  white-space: nowrap;
  text-align: right;
}

.cell-diff.has-diff {
  font-weight: 600;
  color: #c00;
}

.cell-changed {
  text-align: center;
}

.badge-yes {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #ffe0e0;
  color: #c00;
  font-weight: 500;
}

.badge-no {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #e0f0e0;
  color: #2d7d46;
}

.badge-tag {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin: 1px 2px;
}

.badge-composition {
  background: #ffe0e0;
  color: #c00;
}

.badge-price {
  background: #fff3cd;
  color: #856404;
}

.badge-payment_type {
  background: #d6e8ff;
  color: #0050a0;
}

.badge-payment_amount {
  background: #e8d6ff;
  color: #4a0080;
}

.change-line {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 3px;
}

.change-who {
  font-size: 12px;
  color: #555;
}

.report-header {
  margin-bottom: 12px;
}

.report-header h2 {
  font-size: 16px;
  margin: 0;
}

/* Печать: скрыть элементы управления, показать только таблицу */
@media print {
  .no-print {
    display: none !important;
  }

  .print-only {
    display: block !important;
  }

  .kassa-after-redirect {
    padding: 0;
  }

  .report-table {
    font-size: 11px;
  }

  .report-table th,
  .report-table td {
    padding: 4px 6px;
  }

  .order-link {
    color: #000;
    text-decoration: none;
  }
}
</style>
